import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, forkJoin } from 'rxjs';
import { map, catchError, mergeMap, withLatestFrom, filter, tap } from 'rxjs/operators';
import { ProjectService } from './project.service';
import {
  CreateNewProject,
  SetProjectsApiError,
  UpdateProject,
  DeleteProject,
  ProjectCreated,
  LoadProject,
  SetCurrentProject,
  SetLoadProjectError,
  DuplicateProject,
  ShowDeployProjectConfirm,
  SetProjectConfirmCancelled,
  SetProjectGenericError,
  ShowDuplicateProjectDialog,
  LoadProjectSummaries,
  ClosedDuplicateProjectDialog,
  ReceiveProjectSummaries,
  LoadProjectSummary,
  ReceiveProjectSummary,
  ProjectUpdated,
  LazyLoadProjects,
  SetProjectSortOrder,
  ShowDeleteProjectConfirm,
} from './project.actions';
import {
  ProjectApiError,
  ProjectApiErrorTypes,
  ProjectGenericError,
  ProjectGenericErrorTypes,
} from './project.errors';
import { Store } from '@ngrx/store';
import { DeployConfig } from '../config/config.actions';
import { getSortOrder, getProjects, getQuery, selectPreviewProjectId } from './project.selectors';
import { ConfigService } from '../config/config.service';
import { ProjectSummary } from './models/project-summary.model';
import { Project } from './models/project.model';
import { Router } from '@angular/router';

@Injectable()
export class ProjectEffects {
  loadProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadProject),
      mergeMap(({ projectId }) =>
        this.projectService.getProject(projectId).pipe(
          map((project: Project) => SetCurrentProject({ project })),
          catchError((error) => {
            if (error.status === 404) {
              return of(
                SetLoadProjectError({
                  error: new ProjectApiError(ProjectApiErrorTypes.ProjectNotFoundError, error),
                }),
              );
            }
            return of(
              SetLoadProjectError({
                error: new ProjectApiError(ProjectApiErrorTypes.LoadProjectError, error),
              }),
            );
          }),
        ),
      ),
    ),
  );

  lazyLoadProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LazyLoadProjects),
      withLatestFrom(this.store.select(getProjects)),
      // only need to check for `null` initial value
      filter(([, projects]) => !projects),
      map(() => LoadProjectSummaries({ query: null })),
    ),
  );

  setSortOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetProjectSortOrder),
      map(() => LoadProjectSummaries({ query: null })),
    ),
  );

  createNewProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateNewProject),
      withLatestFrom(this.store.select(selectPreviewProjectId)),
      mergeMap(([{ projectSettings, deploy, configSettings, templateId }, previewProjectId]) =>
        this.projectService.createNewProject(projectSettings, previewProjectId).pipe(
          map((project: Project) =>
            ProjectCreated({
              project,
              deploy,
              redirect: `project/${project.id}`,
              configSettings: {
                ...configSettings,
                projectId: project.id,
              },
              templateId,
            }),
          ),
          catchError((error) => {
            const errorType =
              error.status === 409
                ? ProjectApiErrorTypes.DuplicatedProjectError
                : ProjectApiErrorTypes.CreateProjectError;
            return of(SetProjectsApiError({ error: new ProjectApiError(errorType, error) }));
          }),
        ),
      ),
    ),
  );

  duplicateProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DuplicateProject),
      mergeMap(({ projectName, existingProject }) =>
        this.projectService.duplicateProject(existingProject, projectName).pipe(
          tap((project: ProjectSummary) => this.router.navigate(['project', project.id])),
          map(() => LoadProjectSummaries({ query: null })),
          catchError((error) => {
            const errorType =
              error.status === 409
                ? ProjectApiErrorTypes.DuplicatedProjectError
                : ProjectApiErrorTypes.CreateProjectError;
            return of(SetProjectsApiError({ error: new ProjectApiError(errorType, error) }));
          }),
        ),
      ),
    ),
  );

  updateProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateProject),
      withLatestFrom(this.store.select(selectPreviewProjectId)),
      mergeMap(
        ([{ projectSettings, deploy, configSettings, configParameters }, previewProjectId]) =>
          this.projectService.updateProject(projectSettings, previewProjectId).pipe(
            mergeMap(() => [
              ProjectUpdated({
                projectSettings,
                deploy,
                configParameters,
                configSettings,
                redirect: `project/${projectSettings.id}`,
              }),
              LoadProject({ projectId: projectSettings.id }),
              LoadProjectSummaries({ query: null }),
            ]),
            catchError((error) => {
              const errorType =
                error.status === 409
                  ? ProjectApiErrorTypes.DuplicatedProjectError
                  : ProjectApiErrorTypes.UpdateProjectError;
              return of(SetProjectsApiError({ error: new ProjectApiError(errorType, error) }));
            }),
          ),
      ),
    ),
  );

  deleteProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteProject),
      mergeMap(({ projectId }) =>
        this.projectService.deleteProject(projectId).pipe(
          tap(() => this.router.navigate(['dashboard'])),
          map(() => LoadProjectSummaries({ query: null })),
          catchError((error) =>
            of(
              SetProjectsApiError({
                error: new ProjectApiError(ProjectApiErrorTypes.DeleteProjectError, error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  showDeployProjectConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShowDeployProjectConfirm),
      mergeMap(({ project }) =>
        forkJoin([
          of(project),
          this.configService.loadConfig(project.configId),
          this.projectService.showDeployProjectConfirm(project),
        ]),
      ),
      map(([project, config, result]) => {
        if (!config) {
          throw new Error('No project configuration found');
        }
        if (result) {
          return DeployConfig({ config, keyName: project.keyName });
        }
        return SetProjectConfirmCancelled();
      }),
      catchError((error) =>
        of(
          SetProjectGenericError({
            error: new ProjectGenericError(
              ProjectGenericErrorTypes.ShowDeployProjectConfirmError,
              error,
            ),
          }),
        ),
      ),
    ),
  );

  showDeleteProjectConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShowDeleteProjectConfirm),
      mergeMap(({ project }) =>
        forkJoin([of(project), this.projectService.showDeleteProjectConfirm(project)]),
      ),
      map(([project, result]) => {
        if (result) {
          return DeleteProject({ projectId: project.id });
        }
        return SetProjectConfirmCancelled();
      }),
      catchError((error) =>
        of(
          SetProjectGenericError({
            error: new ProjectGenericError(
              ProjectGenericErrorTypes.ShowDeleteProjectConfirmError,
              error,
            ),
          }),
        ),
      ),
    ),
  );

  showDuplicateProjectDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShowDuplicateProjectDialog),
      mergeMap(({ project }) =>
        this.projectService.showDuplicateProjectDialog(project).pipe(
          map((newProject) => {
            if (!newProject) {
              return ClosedDuplicateProjectDialog();
            }

            return DuplicateProject({ existingProject: project, projectName: newProject.name });
          }),
          catchError((err) =>
            of(
              SetProjectGenericError({
                error: new ProjectGenericError(
                  ProjectGenericErrorTypes.ShowDuplicateProjectDialogError,
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadProjectSummaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadProjectSummaries),
      withLatestFrom(this.store.select(getSortOrder), this.store.select(getQuery)),
      mergeMap(([, sortOrder, query]) =>
        this.projectService.getProjectSummaries(sortOrder, query).pipe(
          map((projects: ProjectSummary[]) => ReceiveProjectSummaries({ payload: projects })),
          catchError((err) =>
            of(
              SetProjectsApiError({
                error: new ProjectApiError(ProjectApiErrorTypes.LoadProjectsError, err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadProjectSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadProjectSummary),
      mergeMap(({ projectId }) =>
        this.projectService.getProjectSummary(projectId).pipe(
          map((projectSummary: ProjectSummary) =>
            ReceiveProjectSummary({ payload: projectSummary }),
          ),
          catchError((err) =>
            of(
              SetProjectsApiError({
                error: new ProjectApiError(ProjectApiErrorTypes.LoadProjectSummaryError, err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private projectService: ProjectService,
    private store: Store<any>,
    private configService: ConfigService,
    private router: Router,
  ) {}
}
